import { supabase } from '@/integrations/supabase/client';
import {
  calculatePayroll,
  validatePayrollInput,
  type PayrollCalculationInput,
  type PayrollCalculationResult,
  type EmployeePayrollData
} from './payrollCalculations';
import { secondsToHours } from '@/utils/workTimeUtils';
import { format, startOfMonth, endOfMonth } from 'date-fns';

export interface PayrollPeriod {
  id: string;
  period_year: number;
  period_month: number;
  start_date: string;
  end_date: string;
  status: string;
  created_at: string;
  updated_at: string;
  processed_by?: string;
  processed_at?: string;
}

export interface TimeEntryData {
  user_id: string;
  start_time: string;
  end_time: string;
  duration: number;
}

export class PayrollEngine {
  
  static async getEmployeesForPayroll(): Promise<EmployeePayrollData[]> {
    const { data, error } = await supabase
      .from('employees')
      .select(`
        id,
        name,
        hourly_rate,
        monthly_salary,
        salary_type,
        birth_date,
        tax_canton,
        work_type,
        working_hours_per_week
      `)
      .eq('status', 'active');

    if (error) {
      throw new Error(`Failed to fetch employees: ${error.message}`);
    }

    return data || [];
  }

  static async getTimeEntriesForPeriod(
    employeeId: string,
    startDate: Date,
    endDate: Date
  ): Promise<number> {
    const { data, error } = await supabase
      .from('time_entries')
      .select('duration')
      .eq('user_id', employeeId)
      .gte('start_time', startDate.toISOString())
      .lte('end_time', endDate.toISOString())
      .is('deleted_at', null);

    if (error) {
      throw new Error(`Failed to fetch time entries: ${error.message}`);
    }

    // duration is stored in seconds — convert to hours
    const totalSeconds = data?.reduce((sum, entry) => sum + (entry.duration || 0), 0) || 0;
    return secondsToHours(totalSeconds);
  }

  static async getVacationDaysForPeriod(
    employeeId: string,
    startDate: Date,
    endDate: Date
  ): Promise<number> {
    // TODO: Implement vacation tracking integration
    // For now, return 0 - this will be connected to the vacation system from Phase 3
    return 0;
  }

  static async createPayrollPeriod(year: number, month: number): Promise<PayrollPeriod> {
    const startDate = startOfMonth(new Date(year, month - 1));
    const endDate = endOfMonth(new Date(year, month - 1));

    const { data, error } = await supabase
      .from('payroll_periods')
      .insert({
        period_year: year,
        period_month: month,
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
        status: 'draft'
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create payroll period: ${error.message}`);
    }

    return data;
  }

  static async calculateEmployeePayroll(
    employee: EmployeePayrollData,
    payrollPeriod: PayrollPeriod
  ): Promise<PayrollCalculationResult> {
    // Check for payroll override first
    const { calculateFixedNetPayroll } = await import('./payrollOverrides');
    const fixedResult = await calculateFixedNetPayroll(
      employee.id, 
      employee.birth_date,
      new Date(payrollPeriod.start_date)
    );
    
    if (fixedResult) {
      // Return fixed net calculation as PayrollCalculationResult
      return {
        employee_id: employee.id,
        gross_salary: fixedResult.gross_salary,
        net_salary: fixedResult.net_salary,
        total_deductions: fixedResult.total_deductions,
        hours_worked: 0, // Don't track hours for fixed net
        base_salary: fixedResult.gross_salary,
        deductions: fixedResult.deductions,
        vacation_days_taken: 0,
        vacation_pay_included: false
      };
    }

    // Standard calculation
    const startDate = new Date(payrollPeriod.start_date);
    const endDate = new Date(payrollPeriod.end_date);

    const [hoursWorked, vacationDaysTaken] = await Promise.all([
      this.getTimeEntriesForPeriod(employee.id, startDate, endDate),
      this.getVacationDaysForPeriod(employee.id, startDate, endDate)
    ]);

    const input: PayrollCalculationInput = {
      employee,
      hoursWorked,
      vacationDaysTaken,
      periodStart: startDate,
      periodEnd: endDate
    };

    // Validate input
    const validationErrors = validatePayrollInput(input);
    if (validationErrors.length > 0) {
      throw new Error(`Validation failed: ${validationErrors.join(', ')}`);
    }

    return calculatePayroll(input);
  }

  static async savePayrollCalculation(
    calculation: PayrollCalculationResult,
    payrollPeriodId: string
  ): Promise<void> {
    const { error: calcError } = await supabase
      .from('payroll_calculations')
      .insert({
        employee_id: calculation.employee_id,
        payroll_period_id: payrollPeriodId,
        gross_salary: calculation.gross_salary,
        net_salary: calculation.net_salary,
        total_deductions: calculation.total_deductions,
        hours_worked: calculation.hours_worked,
        base_salary: calculation.base_salary,
        vacation_days_taken: calculation.vacation_days_taken,
        vacation_pay_included: calculation.vacation_pay_included,
        payment_model: 'standard',
        calculation_status: 'calculated'
      });

    if (calcError) {
      throw new Error(`Failed to save payroll calculation: ${calcError.message}`);
    }

    // Save deductions
    if (calculation.deductions.length > 0) {
      const { error: deductionsError } = await supabase
        .from('payroll_deductions')
        .insert(
          calculation.deductions.map(deduction => ({
            payroll_calculation_id: '', // This would need to be the ID from the calculation insert
            deduction_type: deduction.type,
            description: deduction.description,
            rate_percent: deduction.rate_percent,
            base_amount: deduction.base_amount,
            deduction_amount: deduction.deduction_amount
          }))
        );

      if (deductionsError) {
        console.error('Failed to save deductions:', deductionsError);
      }
    }
  }

  static async calculateBulkPayroll(payrollPeriodId: string): Promise<{
    success: PayrollCalculationResult[];
    errors: Array<{ employee: EmployeePayrollData; error: string }>;
  }> {
    const employees = await this.getEmployeesForPayroll();
    
    const { data: period, error: periodError } = await supabase
      .from('payroll_periods')
      .select('*')
      .eq('id', payrollPeriodId)
      .single();

    if (periodError || !period) {
      throw new Error('Payroll period not found');
    }

    const results = {
      success: [] as PayrollCalculationResult[],
      errors: [] as Array<{ employee: EmployeePayrollData; error: string }>
    };

    // Process employees in parallel with controlled concurrency
    const batchSize = 5;
    for (let i = 0; i < employees.length; i += batchSize) {
      const batch = employees.slice(i, i + batchSize);
      
      await Promise.allSettled(
        batch.map(async (employee) => {
          try {
            const calculation = await this.calculateEmployeePayroll(employee, period);
            await this.savePayrollCalculation(calculation, payrollPeriodId);
            results.success.push(calculation);
          } catch (error) {
            results.errors.push({
              employee,
              error: error instanceof Error ? error.message : 'Unknown error'
            });
          }
        })
      );
    }

    // Update period status
    await supabase
      .from('payroll_periods')
      .update({ 
        status: results.errors.length === 0 ? 'calculated' : 'draft',
        updated_at: new Date().toISOString()
      })
      .eq('id', payrollPeriodId);

    return results;
  }

  static async approvePayrollPeriod(payrollPeriodId: string, approvedBy: string): Promise<void> {
    const { error } = await supabase
      .from('payroll_periods')
      .update({ 
        status: 'approved',
        processed_by: approvedBy,
        processed_at: new Date().toISOString()
      })
      .eq('id', payrollPeriodId);

    if (error) {
      throw new Error(`Failed to approve payroll period: ${error.message}`);
    }

    // Update all calculations in this period to approved
    await supabase
      .from('payroll_calculations')
      .update({ 
        calculation_status: 'approved',
        approved_at: new Date().toISOString(),
        approved_by: approvedBy
      })
      .eq('payroll_period_id', payrollPeriodId);
  }

  static async getPayrollPeriods(): Promise<PayrollPeriod[]> {
    const { data, error } = await supabase
      .from('payroll_periods')
      .select('*')
      .order('period_year', { ascending: false })
      .order('period_month', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch payroll periods: ${error.message}`);
    }

    return data || [];
  }

  static async getPayrollCalculationsForPeriod(payrollPeriodId: string) {
    const { data, error } = await supabase
      .from('payroll_calculations')
      .select(`
        *,
        employees (
          name,
          email
        )
      `)
      .eq('payroll_period_id', payrollPeriodId);

    if (error) {
      throw new Error(`Failed to fetch payroll calculations: ${error.message}`);
    }

    return data || [];
  }
}